import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { apiGet } from '../utils/api'

interface ProfitImpactDocument {
  id: string
  date: string
  type: string
  documentType: string
  documentNumber: string
  description: string
  amount: number
  impact: 'ДОХОД' | 'РАСХОД'
  tournamentId?: string | null
  article?: string
  details: Array<{
    itemName: string
    quantity: number
    unitPrice: number
    totalPrice: number
  }>
}

interface ProfitImpactSummary {
  totalIncome: number
  totalExpense: number
  totalProfit: number
  documentsCount: number
  incomeDocumentsCount: number
  expenseDocumentsCount: number
}

const ReportsProfitImpact: React.FC = () => {
  const [documents, setDocuments] = useState<ProfitImpactDocument[]>([])
  const [summary, setSummary] = useState<ProfitImpactSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedDocument, setSelectedDocument] = useState<ProfitImpactDocument | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [lastValidFrom, setLastValidFrom] = useState('')
  const [lastValidTo, setLastValidTo] = useState('')
  
  // Новые фильтры
  const [documentType, setDocumentType] = useState('')
  const [impact, setImpact] = useState('')
  const [tournament, setTournament] = useState('')
  const [tournaments, setTournaments] = useState<Array<{id: string, name: string}>>([])
  
  // Загружаем сохраненный период при инициализации
  useEffect(() => {
    const savedFrom = localStorage.getItem('profit-impact-report-from')
    const savedTo = localStorage.getItem('profit-impact-report-to')
    if (savedFrom) setDateFrom(savedFrom)
    if (savedTo) setDateTo(savedTo)
    setIsInitialized(true)
  }, [])

  // Загружаем турниры
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        // Загружаем все турниры (без пагинации)
        const data = await apiGet<any>('/tournaments', { limit: '1000' })
        setTournaments(data.tournaments || [])
      } catch (error) {
        console.error('Ошибка загрузки турниров:', error)
      }
    }
    fetchTournaments()
  }, [])

  // Загружаем данные только при реальном изменении дат (не при перелистывании календаря)
  useEffect(() => {
    if (isInitialized) {
      // Проверяем, что дата действительно изменилась и является валидной
      const fromChanged = dateFrom !== lastValidFrom && dateFrom !== ''
      const toChanged = dateTo !== lastValidTo && dateTo !== ''
      
      // Также загружаем если обе даты пустые (сброс)
      const bothEmpty = dateFrom === '' && dateTo === '' && (lastValidFrom !== '' || lastValidTo !== '')
      
      // Загружаем при инициализации если нет сохраненных дат
      const initialLoad = dateFrom === '' && dateTo === '' && lastValidFrom === '' && lastValidTo === ''
      
      if (fromChanged || toChanged || bothEmpty || initialLoad) {
        // Небольшая задержка для предотвращения множественных вызовов
        const timer = setTimeout(() => {
          fetchData()
          setLastValidFrom(dateFrom)
          setLastValidTo(dateTo)
        }, 300)
        
        return () => clearTimeout(timer)
      }
    }
  }, [isInitialized, dateFrom, dateTo, lastValidFrom, lastValidTo])

  // Загружаем данные при изменении фильтров
  useEffect(() => {
    if (isInitialized) {
      const timer = setTimeout(() => {
        fetchData()
      }, 300)
      
      return () => clearTimeout(timer)
    }
  }, [documentType, impact, tournament])

  // Сохраняем период в localStorage
  const savePeriod = (fromDate: string, toDate: string) => {
    if (fromDate) {
      localStorage.setItem('profit-impact-report-from', fromDate)
    } else {
      localStorage.removeItem('profit-impact-report-from')
    }
    if (toDate) {
      localStorage.setItem('profit-impact-report-to', toDate)
    } else {
      localStorage.removeItem('profit-impact-report-to')
    }
  }

  // Сброс периода
  const resetPeriod = () => {
    setDateFrom('')
    setDateTo('')
    setDocumentType('')
    setImpact('')
    setTournament('')
    setLastValidFrom('')
    setLastValidTo('')
    localStorage.removeItem('profit-impact-report-from')
    localStorage.removeItem('profit-impact-report-to')
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (dateFrom) params.from = dateFrom
      if (dateTo) params.to = dateTo
      if (documentType) params.documentType = documentType
      if (impact) params.impact = impact
      if (tournament) params.tournament = tournament
      
      const data = await apiGet<any>('/reports/profit-impact-documents', params)
      
      setDocuments(data.documents || [])
      setSummary(data.summary || null)
    } catch (error) {
      console.error('Ошибка загрузки отчета:', error)
      setDocuments([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'MDL',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const getImpactColor = (impact: string) => {
    return impact === 'ДОХОД' ? 'text-green-600' : 'text-red-600'
  }

  const getImpactBgColor = (impact: string) => {
    return impact === 'ДОХОД' ? 'bg-green-50' : 'bg-red-50'
  }

  // Функция для определения цвета суммы на основе влияния
  const getAmountColor = (impact: string) => {
    return impact === 'РАСХОД' ? 'text-red-600' : 'text-green-600'
  }

  const getDocumentTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      'CONSUMPTION': 'bg-orange-100 text-orange-800',
      'RETURN': 'bg-green-100 text-green-800',
      'INVENTORY_SALE': 'bg-blue-100 text-blue-800',
      'INVENTORY_SALE_COST': 'bg-indigo-100 text-indigo-800',
      'PLAYER_PAYMENT': 'bg-purple-100 text-purple-800',
      'SERVICE_PURCHASE': 'bg-yellow-100 text-yellow-800',
      'CANCELLATION': 'bg-red-100 text-red-800',
      'DISCOUNT': 'bg-pink-100 text-pink-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const truncateDocumentNumber = (number: string, maxLength: number = 12) => {
    return number.length > maxLength ? `${number.substring(0, maxLength)}...` : number
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900 mb-3">
          Документы, влияющие на прибыль
        </h1>
        
        {/* Фильтры */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              От даты
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              До даты
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => { savePeriod(dateFrom, dateTo); fetchData(); }}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              Обновить
            </button>
            <button
              onClick={resetPeriod}
              className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-500"
            >
              Сбросить
            </button>
          </div>
          
          {/* Дополнительные фильтры справа */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Тип документа
            </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Все типы</option>
                <option value="CONSUMPTION">Расход ТМЦ</option>
                <option value="RETURN">Возврат ТМЦ</option>
                <option value="INVENTORY_SALE">Продажа ТМЦ</option>
                <option value="INVENTORY_SALE_COST">Стоимость продажи ТМЦ</option>
                <option value="PLAYER_PAYMENT">Взнос игрока</option>
                <option value="SERVICE_PURCHASE">Закупка услуг</option>
                <option value="CANCELLATION">Отмена участия</option>
                <option value="DISCOUNT">Скидка игроку</option>
              </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Влияние
            </label>
            <select
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Все</option>
              <option value="ДОХОД">Доход</option>
              <option value="РАСХОД">Расход</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Турнир
            </label>
              <select
                value={tournament}
                onChange={(e) => setTournament(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Все турниры</option>
                <option value="no-tournament">Без турнира</option>
                {tournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
          </div>
        </div>

        {/* Сводка */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white p-3 rounded shadow border">
              <div className="text-xs font-medium text-gray-500">Общий доход</div>
              <div className={`text-lg font-bold ${summary.totalIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.totalIncome)}
              </div>
            </div>
            <div className="bg-white p-3 rounded shadow border">
              <div className="text-xs font-medium text-gray-500">Общий расход</div>
              <div className="text-lg font-bold text-red-600">
                {formatCurrency(summary.totalExpense)}
              </div>
            </div>
            <div className="bg-white p-3 rounded shadow border">
              <div className="text-xs font-medium text-gray-500">Прибыль</div>
              <div className={`text-lg font-bold ${summary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.totalProfit)}
              </div>
            </div>
            <div className="bg-white p-3 rounded shadow border">
              <div className="text-xs font-medium text-gray-500">Всего документов</div>
              <div className="text-lg font-bold text-gray-900">
                {summary.documentsCount}
              </div>
              <div className="text-xs text-gray-500">
                Доходы: {summary.incomeDocumentsCount} | Расходы: {summary.expenseDocumentsCount}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Таблица документов */}
      <div className="bg-white rounded shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Тип
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Номер
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Описание
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Влияние
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статья
                </th>
                       <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Сумма
                       </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-3 text-center text-gray-500 text-sm">
                    Загрузка...
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-3 text-center text-gray-500 text-sm">
                    Нет документов за выбранный период
                  </td>
                </tr>
                     ) : (
                       documents.map((doc) => (
                         <tr 
                           key={doc.id} 
                           className="hover:bg-gray-50 cursor-pointer"
                           onClick={() => setSelectedDocument(doc)}
                         >
                           <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                             {format(new Date(doc.date), 'dd.MM.yy', { locale: ru })}
                           </td>
                           <td className="px-3 py-2 whitespace-nowrap">
                             <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${getDocumentTypeColor(doc.type)}`}>
                               {doc.documentType}
                             </span>
                           </td>
                           <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900 font-mono">
                             {truncateDocumentNumber(doc.documentNumber)}
                           </td>
                           <td className="px-3 py-2 text-xs text-gray-900 max-w-xs truncate">
                             {doc.description}
                           </td>
                           <td className="px-3 py-2 whitespace-nowrap">
                             <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${getImpactBgColor(doc.impact)} ${getImpactColor(doc.impact)}`}>
                               {doc.impact}
                             </span>
                           </td>
                           <td className="px-3 py-2 text-xs text-gray-900 max-w-xs truncate">
                             {doc.article || '-'}
                           </td>
                           <td className={`px-3 py-2 whitespace-nowrap text-xs font-medium ${getAmountColor(doc.impact)}`}>
                             {formatCurrency(Math.abs(doc.amount))}
                           </td>
                         </tr>
                       ))
                     )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Модальное окно с деталями */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Детали документа: {selectedDocument.documentType}
                </h3>
                <button
                  onClick={() => setSelectedDocument(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Закрыть</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Номер:</span>
                    <span className="ml-2 text-gray-900">{selectedDocument.documentNumber}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Дата:</span>
                    <span className="ml-2 text-gray-900">
                      {format(new Date(selectedDocument.date), 'dd.MM.yyyy', { locale: ru })}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Влияние:</span>
                    <span className={`ml-2 font-medium ${getImpactColor(selectedDocument.impact)}`}>
                      {selectedDocument.impact}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Сумма:</span>
                    <span className={`ml-2 font-medium ${getAmountColor(selectedDocument.impact)}`}>
                      {formatCurrency(Math.abs(selectedDocument.amount))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Описание:</h4>
                <p className="text-sm text-gray-600">{selectedDocument.description}</p>
              </div>

              {selectedDocument.details && selectedDocument.details.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Детализация:</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Наименование
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Количество
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Цена за ед.
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Сумма
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedDocument.details.map((detail, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {detail.itemName}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {detail.quantity}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {formatCurrency(detail.unitPrice)}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">
                              {formatCurrency(detail.totalPrice)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedDocument(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportsProfitImpact

